import {
  BufferGeometry,
  Color,
  DoubleSide,
  Line,
  LineBasicMaterial,
  LineLoop,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Raycaster,
  Scene,
  Texture,
  Vector3,
} from "three";
import { AutoAttenuablePin } from "../object/AutoAttenuablePin";
import { MEASUREMENT_SPHERE_SIZE } from "../config/ViewerConfig";
import { AutoAttenuatableSphere } from "./AutoAttenuatableSphere";
import { triangulateXY } from "../utils/utils";
import { AutoAttenuableLine } from "./AutoAttenuableLine";
import { Viewer } from "../viewer";

export enum Previewable {
  SPHERE = "sphere",
  LINE = "line",
  PIN = "pin",
  MESH = "mesh",
  LINE_LOOP = "line-loop",
  THICK_LINE = "thick_line",

  NONE = "none",
}

export interface IPreviewable {
  type: Previewable;
  visible?: boolean;
  scale?: Vector3;
  color?: Color;
}
export interface IPreviewSphere extends IPreviewable {
  position?: Vector3;
}

export interface IPreviewPin extends IPreviewable {
  position?: Vector3;
  texture?: Texture;
}

export interface IPreviewLine extends IPreviewable {
  points?: Vector3[];
}
export interface IPreviewAutoAtenuableThickLine extends IPreviewable {
  point1: Vector3;
  point2: Vector3;
}
export interface IPreviewLineLoop extends IPreviewable {
  points?: Vector3[];
}
export interface IPreviewMesh extends IPreviewable {
  points?: Vector3[];
}

type TPreviewType =
  | IPreviewSphere
  | IPreviewPin
  | IPreviewLine
  | IPreviewLineLoop
  | IPreviewMesh
  | IPreviewAutoAtenuableThickLine;

function* PreviewIdGenerator() {
  let count = 0;
  while (true) {
    yield count++;
  }
}

export const generatePreviewId = PreviewIdGenerator();

export class PreviewManager {
  // private _group: Group;
  private _viewer: Viewer;
  private _scene: Scene;
  private _previewableMap: Map<number, { type: Previewable; mesh: Object3D }>;

  constructor(scene: Scene, viewer: Viewer) {
    this._scene = scene;
    this._viewer = viewer;
    this._previewableMap = new Map();
  }

  private createPreviewable(preview: {
    type: Previewable;
    count: number;
    color: number;
  }): { type: Previewable; ids: number[] } {
    const result: { type: Previewable; ids: number[] } = {
      type: Previewable.NONE,
      ids: [],
    };
    switch (preview.type) {
      case Previewable.SPHERE:
        const sphereIds = [];
        for (let i = 0; i < preview.count; ++i) {
          const sphereMaterial = new MeshBasicMaterial({
            color: preview.color,
            depthTest: false,
          });
          const sphere = new AutoAttenuatableSphere(
            sphereMaterial,
            MEASUREMENT_SPHERE_SIZE
          ); //optimization - instances can be used
          sphere.visible = false;

          this._scene.add(sphere);
          const id = generatePreviewId.next().value as number;
          this._previewableMap.set(id, {
            type: Previewable.SPHERE,
            mesh: sphere,
          });
          sphereIds.push(id);
        }
        result.type = Previewable.SPHERE;
        result.ids = sphereIds;
        break;
      case Previewable.LINE:
        const lineIds = [];
        for (let i = 0; i < preview.count; ++i) {
          const lineGeometry = new BufferGeometry();
          const lineMaterial = new LineBasicMaterial({
            color: preview.color,
            depthTest: false,
          });
          const line = new Line(lineGeometry, lineMaterial);
          line.visible = false;
          this._scene.add(line);
          const id = generatePreviewId.next().value as number;
          this._previewableMap.set(id, {
            type: Previewable.LINE,
            mesh: line,
          });
          lineIds.push(id);
        }
        result.type = Previewable.LINE;
        result.ids = lineIds;
        break;
      case Previewable.LINE_LOOP:
        const lineloopIds = [];
        for (let i = 0; i < preview.count; ++i) {
          const lineGeometry = new BufferGeometry();
          const lineMaterial = new LineBasicMaterial({
            color: preview.color,
            depthTest: false,
          });
          const line = new LineLoop(lineGeometry, lineMaterial);
          line.visible = false;
          this._scene.add(line);
          const id = generatePreviewId.next().value as number;
          this._previewableMap.set(id, {
            type: Previewable.LINE_LOOP,
            mesh: line,
          });
          lineloopIds.push(id);
        }
        result.type = Previewable.LINE_LOOP;
        result.ids = lineloopIds;
        break;
      case Previewable.PIN:
        const pinIds = [];
        for (let i = 0; i < preview.count; ++i) {
          const pin = new AutoAttenuablePin(`${i}`, this._viewer); //optimization - instances can be used
          pin.visible = false;
          // pin.scale.set(0.1, 0.1, 0.1);
          this._scene.add(pin);
          const id = generatePreviewId.next().value as number;
          this._previewableMap.set(id, {
            type: Previewable.PIN,
            mesh: pin,
          });
          pinIds.push(id);
        }
        result.type = Previewable.PIN;
        result.ids = pinIds;
        break;
      case Previewable.MESH:
        const meshIds = [];
        for (let i = 0; i < preview.count; ++i) {
          const mesh = new Mesh(
            new BufferGeometry(),
            new MeshBasicMaterial({
              color: preview.color,
              side: DoubleSide,
              opacity: 0.5,
              transparent: true,
            })
          ); //optimization - instances can be used
          mesh.visible = false;
          // mesh.scale.set(0.1, 0.1, 0.1);
          this._scene.add(mesh);
          const id = generatePreviewId.next().value as number;
          this._previewableMap.set(id, {
            type: Previewable.MESH,
            mesh: mesh,
          });
          meshIds.push(id);
        }
        result.type = Previewable.MESH;
        result.ids = meshIds;
        break;
      case Previewable.THICK_LINE:
        const thicklineIds = [];
        for (let i = 0; i < preview.count; ++i) {
          const lineMaterial = new MeshBasicMaterial({
            color: preview.color,
            depthTest: false,
          });
          const line = new AutoAttenuableLine(0, lineMaterial);
          line.visible = false;
          this._scene.add(line);
          const id = generatePreviewId.next().value as number;
          this._previewableMap.set(id, {
            type: Previewable.THICK_LINE,
            mesh: line,
          });
          thicklineIds.push(id);
        }
        result.type = Previewable.THICK_LINE;
        result.ids = thicklineIds;
        break;
    }
    return result;
  }

  public init(
    previewables: { type: Previewable; count: number; color: number }[]
  ): { type: Previewable; ids: number[] }[] {
    const result: { type: Previewable; ids: number[] }[] = [];
    for (const preview of previewables) {
      result.push(this.createPreviewable(preview));
    }
    return result;
  }

  public addPreviewable(previewable: {
    type: Previewable;
    count: number;
    color: number;
  }): { type: Previewable; ids: number[] } {
    return this.createPreviewable(previewable);
  }

  public dispose(ids?: number[]) {
    const _disposeMesh = (obj: Mesh) => {
      this._scene.remove(obj);
      if (obj.material) {
        (obj.material as MeshBasicMaterial).dispose();
        obj.material = undefined;
      }
      obj.geometry.dispose();
      obj.geometry = undefined;
      (obj as any)["dispose"] && (obj as any)["dispose"]();
    };

    if (ids) {
      for (const id of ids) {
        const obj = this._previewableMap.get(id);
        if (obj && obj.mesh) {
          _disposeMesh(obj.mesh as Mesh);
          this._previewableMap.delete(id);
        }
      }
    } else {
      this._previewableMap.forEach((obj) => {
        _disposeMesh(obj.mesh as Mesh);
      });
      this._previewableMap.clear();
    }
  }

  updatePreview(id: number, params: TPreviewType) {
    const previewable = this._previewableMap.get(id);
    if (previewable) {
      previewable.mesh.visible =
        params.visible === undefined
          ? previewable.mesh.visible
          : params.visible;
      params.scale && previewable.mesh.scale.copy(params.scale);
      params.color &&
        ((previewable.mesh as Mesh).material as MeshBasicMaterial).color.copy(
          params.color
        );
      switch (previewable.type) {
        case Previewable.SPHERE: {
          (params as IPreviewSphere).position &&
            previewable.mesh.position.copy((params as IPreviewSphere).position);
          break;
        }
        case Previewable.LINE: {
          (params as IPreviewLine).points &&
            (previewable.mesh as Line).geometry.setFromPoints(
              (params as IPreviewLine).points
            );
          break;
        }
        case Previewable.PIN: {
          (params as IPreviewPin).position &&
            (previewable.mesh as AutoAttenuablePin).setPosition(
              (params as IPreviewPin).position
            );
          break;
        }
        case Previewable.LINE_LOOP: {
          (params as IPreviewLineLoop).points &&
            (previewable.mesh as LineLoop).geometry.setFromPoints(
              (params as IPreviewLineLoop).points
            );
          break;
        }
        case Previewable.MESH: {
          (params as IPreviewMesh).points &&
            (previewable.mesh as Mesh).geometry.setFromPoints(
              (params as IPreviewMesh).points
            );
          // const indices: number[]  = []
          // Array((params as IPreviewMesh).points.length -2).fill(0).forEach((_v,index)=> indices.push(index +1 ,index+2,0));
          const indices = triangulateXY((params as IPreviewMesh).points);
          (previewable.mesh as Mesh).geometry.setIndex(indices);
          (previewable.mesh as Mesh).geometry.getAttribute(
            "position"
          ).needsUpdate = true;
          (previewable.mesh as Mesh).geometry.index.needsUpdate = true;
          break;
        }
        case Previewable.THICK_LINE: {
          const _params = params as IPreviewAutoAtenuableThickLine;
          _params.point1 && _params.point2 &&
            (previewable.mesh as AutoAttenuableLine).updatePoints(_params.point1, _params.point2);
          break;
        }
      }
    }
  }

  public getSpherePosition(id: number): Vector3 {
    const sphere = this._previewableMap.get(id);
    if (sphere && sphere.type === Previewable.SPHERE) {
      return sphere.mesh.position;
    }
  }

  public getPinPosition(id: number): Vector3 {
    const pin = this._previewableMap.get(id);
    if (pin && pin.type === Previewable.PIN) {
      return (pin.mesh as AutoAttenuablePin).getPosition();
    }
  }

  public getLinePoints(id: number): Vector3[] {
    const line = this._previewableMap.get(id);
    if (line && line.type === Previewable.LINE) {
      const flatPoints = (line.mesh as Line).geometry.getAttribute(
        "position"
      ).array;
      const result: Vector3[] = [];
      for (let i = 0; i < flatPoints.length; i += 3) {
        result.push(
          new Vector3(flatPoints[i], flatPoints[i + 1], flatPoints[i + 2])
        );
      }
      return result;
    }
  }

  public getPin(id: number): Object3D {
    return this._previewableMap.get(id).mesh;
  }
}
